import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { CircularProgress } from "@mui/material";
import { baseUrl } from "../../../../helper/urls";
import { useSelector } from "react-redux";

const EditCounselor = () => {
  const { token } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    profileImage: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country_code: "",
    universityName: "",
    satActScore: "",
    majorCourse: "",
    interestedIn: "General Counseling",
    testimony: "",
    pricePerHour: "",
  });

  const [loading, setLoading] = useState(false);

  const fetchCounselorById = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/counselors/id/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data.data;

      setFormData({
        profileImage: "",
        firstName: data?.firstName || "",
        lastName: data?.lastName || "",
        email: data?.email || "",
        phone: data?.phone || "",
        country_code: data?.country_code || "",
        universityName: data?.universityName || "",
        satActScore: data?.satActScore || "",
        majorCourse: data?.majorCourse || "",
        interestedIn: data?.interestedIn || "General Counseling",
        testimony: data?.testimony || "",
        pricePerHour: data?.price_per_hour || "",
      });
    } catch (error) {
      toast.error("Failed to fetch counselor data.");
      console.error("Error fetching counselor data:", error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCounselorById();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handlePhoneChange = (value, country) => {
    setFormData((prevData) => ({
      ...prevData,
      phone: value,
      country_code: `+${country.dialCode}`,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(
        `${baseUrl}/api/counselors/update/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Counselor updated successfully!");
        setTimeout(() => {
          navigate("/all-counselors");
        }, 2000);
      } else {
        toast.error("Failed to update counselor.");
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data?.msg || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Toastify container */}
      <ToastContainer />

      <div className="card h-100 p-0 radius-12">
        <div className="row justify-content-center">
          <div className="col-xxl-12 col-xl-12 col-lg-12">
            <div className="card border">
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    {/* First Name */}
                    <div className="col-sm-6">
                      <label className="form-label">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Last Name */}
                    <div className="col-sm-6">
                      <label className="form-label">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Email */}
                    <div className="col-sm-6">
                      <label className="form-label">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Phone */}
                    <div className="col-sm-6">
                      <label className="form-label">
                        Phone <span className="text-danger">*</span>
                      </label>
                      <div className="custom-phone-input-wrapper">
                        <PhoneInput
                          country={"us"}
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          inputProps={{
                            required: true,
                            name: "phone",
                            className: "form-control radius-8",
                          }}
                        />
                      </div>
                    </div>

                    {/* University */}
                    <div className="col-sm-6">
                      <label className="form-label">
                        University Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="universityName"
                        value={formData.universityName}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* SAT/ACT Score */}
                    <div className="col-sm-6">
                      <label className="form-label">
                        SAT/ACT Score <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        name="satActScore"
                        value={formData.satActScore}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Major Course */}
                    <div className="col-sm-6">
                      <label className="form-label">Major Course</label>
                      <input
                        type="text"
                        className="form-control"
                        name="majorCourse"
                        value={formData.majorCourse}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Interested In */}
                    <div className="col-sm-6">
                      <label className="form-label">
                        Interested In <span className="text-danger">*</span>
                      </label>
                      <select
                        name="interestedIn"
                        className="form-control"
                        value={formData.interestedIn}
                        onChange={handleChange}
                        required
                      >
                        <option value="General Counseling">
                          General Counseling
                        </option>
                        <option value="College Counseling">
                          College Counseling
                        </option>
                        <option value="Essay Counseling">
                          Essay Counseling
                        </option>
                        <option value="Essay Collage Counseling">
                          Essay + College Counseling
                        </option>
                        <option value="Essay General Counseling">
                          Essay + General Counseling
                        </option>
                        <option value="College Tour">College Tour</option>
                      </select>
                    </div>

                    {/* Submit Button */}
                    <div className="col-sm-12 mt-3">
                      <button
                        className="btn btn-primary rounded-pill px-5 py-2"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Update"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom styles */}
      <style>{`
        .custom-phone-input-wrapper .form-control {
          width: 100% !important;
          height: 45px;
          font-size: 16px;
        }

        .custom-phone-input-wrapper .flag-dropdown {
          height: 100%;
          border-radius: 8px 0 0 8px;
        }

        @media (max-width: 768px) {
          .custom-phone-input-wrapper .form-control {
            height: 40px;
            font-size: 14px;
          }
        }

        @media (max-width: 576px) {
          .custom-phone-input-wrapper .form-control {
            height: 36px;
            font-size: 13px;
          }
        }
      `}</style>
    </>
  );
};

export default EditCounselor;

// import React, { useState, useEffect } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import axios from "axios";
// import { toast, ToastContainer } from "react-toastify";
// import PhoneInput from "react-phone-input-2";
// import "react-phone-input-2/lib/style.css";
// import { CircularProgress } from "@mui/material";
// import { baseUrl } from "../../../../helper/urls";
// import { useSelector } from "react-redux";
// const EditCounselor = () => {
//   const { token } = useSelector((state) => state.auth);
//   const navigate = useNavigate();
//   const { id } = useParams();

//   const [formData, setFormData] = useState({
//     profileImage: "",
//     firstName: "",
//     lastName: "",
//     email: "",
//     phone: "",
//     country_code: "",
//     universityName: "",
//     satActScore: "",
//     majorCourse: "",
//     interestedIn: "General Counseling",
//     testimony: "",
//     pricePerHour: "",
//   });

//   const [loading, setLoading] = useState(false);

//   const fetchCounselorById = async () => {
//     try {
//       const response = await axios.get(`${baseUrl}/api/counselors/id/${id}`, {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });

//       const data = response.data.data;

//       setFormData({
//         profileImage: "",
//         firstName: data?.firstName || "",
//         lastName: data?.lastName || "",
//         email: data?.email || "",
//         phone: data?.phone || "",
//         country_code: data?.country_code || "",
//         universityName: data?.universityName || "",
//         satActScore: data?.satActScore || "",
//         majorCourse: data?.majorCourse || "",
//         interestedIn: data?.interestedIn || "General Counseling",
//         testimony: data?.testimony || "",
//         pricePerHour: data?.price_per_hour || "",
//       });
//     } catch (error) {
//       toast.error("Failed to fetch counselor data.");
//       console.error("Error fetching counselor data:", error);
//     }
//   };

//   useEffect(() => {
//     if (id) {
//       fetchCounselorById();
//     }
//   }, [id]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prevData) => ({
//       ...prevData,
//       [name]: value,
//     }));
//   };

//   const handlePhoneChange = (value, country) => {
//     setFormData((prevData) => ({
//       ...prevData,
//       phone: value,
//       country_code: `+${country.dialCode}`,
//     }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);

//     try {
//       const response = await axios.put(
//         `${baseUrl}/api/counselors/update/${id}`,
//         formData,
//         {
//           headers: {
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );

//       if (response.status === 200 || response.status === 201) {
//         toast.success("Counselor updated successfully!");
//         setTimeout(() => {
//           navigate("/all-counselors");
//         }, 2000);
//       } else {
//         toast.error("Failed to update counselor.");
//       }
//     } catch (error) {
//       console.error("Update error:", error);
//       toast.error(error.response?.data?.msg || "Something went wrong.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <>
//       <ToastContainer position="top-right" autoClose={2000} />
//       <div className="card h-100 p-0 radius-12">
//         <div className="row justify-content-center">
//           <div className="col-xxl-12 col-xl-12 col-lg-12">
//             <div className="card border">
//               <div className="card-body">
//                 <form onSubmit={handleSubmit}>
//                   <div className="row">
//                     {/* First Name */}
//                     <div className="col-sm-6">
//                       <label className="form-label">
//                         First Name <span className="text-danger-600">*</span>
//                       </label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         name="firstName"
//                         value={formData.firstName}
//                         onChange={handleChange}
//                         required
//                       />
//                     </div>

//                     {/* Last Name */}
//                     <div className="col-sm-6">
//                       <label className="form-label">
//                         Last Name <span className="text-danger-600">*</span>
//                       </label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         name="lastName"
//                         value={formData.lastName}
//                         onChange={handleChange}
//                         required
//                       />
//                     </div>

//                     {/* Email */}
//                     <div className="col-sm-6">
//                       <label className="form-label">
//                         Email <span className="text-danger-600">*</span>
//                       </label>
//                       <input
//                         type="email"
//                         className="form-control"
//                         name="email"
//                         value={formData.email}
//                         onChange={handleChange}
//                         required
//                       />
//                     </div>

//                     {/* Phone */}
//                     <div className="col-sm-6">
//                       <label className="form-label">
//                         Phone <span className="text-danger-600">*</span>
//                       </label>
//                       <div className="custom-phone-input-wrapper">
//                         <PhoneInput
//                           country={"us"}
//                           value={formData.phone}
//                           onChange={handlePhoneChange}
//                           inputProps={{
//                             required: true,
//                             name: "phone",
//                             className: "form-control radius-8",
//                           }}
//                         />
//                       </div>
//                     </div>

//                     {/* University */}
//                     <div className="col-sm-6">
//                       <label className="form-label">
//                         University Name{" "}
//                         <span className="text-danger-600">*</span>
//                       </label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         name="universityName"
//                         value={formData.universityName}
//                         onChange={handleChange}
//                         required
//                       />
//                     </div>

//                     {/* SAT/ACT Score */}
//                     <div className="col-sm-6">
//                       <label className="form-label">
//                         SAT/ACT Score <span className="text-danger-600">*</span>
//                       </label>
//                       <input
//                         type="number"
//                         className="form-control"
//                         name="satActScore"
//                         value={formData.satActScore}
//                         onChange={handleChange}
//                         required
//                       />
//                     </div>

//                     {/* Major Course */}
//                     <div className="col-sm-6">
//                       <label className="form-label">Major Course</label>
//                       <input
//                         type="text"
//                         className="form-control"
//                         name="majorCourse"
//                         value={formData.majorCourse}
//                         onChange={handleChange}
//                         required
//                       />
//                     </div>

//                     {/* Interested In */}
//                     <div className="col-sm-6">
//                       <label className="form-label">
//                         Interested In <span className="text-danger-600">*</span>
//                       </label>
//                       <select
//                         name="interestedIn"
//                         className="form-control"
//                         value={formData.interestedIn}
//                         onChange={handleChange}
//                         required
//                       >
//                         <option value="General Counseling">
//                           General Counseling
//                         </option>
//                         <option value="College Counseling">
//                           College Counseling
//                         </option>
//                         <option value="Essay Counseling">
//                           Essay Counseling
//                         </option>
//                         <option value="Essay Collage Counseling">
//                           Essay + College Counseling
//                         </option>
//                         <option value="Essay General Counseling">
//                           Essay + General Counseling
//                         </option>
//                         <option value="College Tour">College Tour</option>
//                       </select>
//                     </div>

//                     {/* Submit */}
//                     <div className="col-sm-12 mt-3">
//                       <button
//                         className="btn btn-primary rounded-pill px-5 py-2"
//                         type="submit"
//                         disabled={loading}
//                       >
//                         {loading ? (
//                           <CircularProgress size={24} color="inherit" />
//                         ) : (
//                           "Update"
//                         )}
//                       </button>
//                     </div>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       <style>{`
//         .custom-phone-input-wrapper .form-control {
//           width: 100% !important;
//           height: 45px;
//           font-size: 16px;
//         }

//         .custom-phone-input-wrapper .flag-dropdown {
//           height: 100%;
//           border-radius: 8px 0 0 8px;
//         }

//         @media (max-width: 768px) {
//           .custom-phone-input-wrapper .form-control {
//             height: 40px;
//             font-size: 14px;
//           }
//         }

//         @media (max-width: 576px) {
//           .custom-phone-input-wrapper .form-control {
//             height: 36px;
//             font-size: 13px;
//           }
//         }
//       `}</style>
//     </>
//   );
// };

// export default EditCounselor;
